import TenantName from "components/TenantName";
import WaitingDots from "components/WaitingDots";
import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import React from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    invoice: Invoice;
    accounts: Account[];
    style?: CSSProperties;
}

const InvoiceRowIndex: React.FC<Props> = ({ invoice, accounts, style }) => {
    const { intlLocale, t } = useOfficeTranslation();
    if (invoice === undefined) {
        return <WaitingDots />;
    }

    const date = (value: Date) => new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(value);
    const currency = (value: number) => new Intl.NumberFormat(intlLocale, {style: "currency", currency: "EUR"}).format(value);
    const customerName = accounts.find(account => account.id === invoice.customer)?.legal_name ?? t("common.unknownAccount");
    const supplierName = accounts.find(account => account.id === invoice.supplier)?.legal_name ?? t("common.unknownSupplier");
    const status = invoice.quote
        ? <span className="badge bg-primary bg-soft text-primary">{t("common.quote")}</span>
        : invoice.draft
            ? <span className="badge bg-primary bg-soft text-primary">{t("common.draft")}</span>
            : invoice.IsPayWhenPaid()
                ? <span className="badge bg-warning bg-soft text-warning">{t("common.payWhenPaid")}</span>
            : <span className={`badge ${invoice.IsPaid()
                ? "bg-success bg-soft text-success"
                : "bg-danger bg-soft text-danger"
            }`}>
                {date(invoice.GetDeadlineDate())}
            </span>;

    return (
        <>
            <div className="d-none d-md-block border-bottom py-3" style={style}>
                <Row className="align-items-center g-3">
                    <Col md={2}>
                        <Link className="text-body text-decoration-none" to={`/invoice/${invoice.id}`}>
                            <strong>{invoice.GetDisplayName() || <i>{t("common.noName")}</i>}</strong>
                        </Link>
                    </Col>
                    <Col md={4}>
                        <div className="d-flex flex-wrap gap-1 mb-1">
                            <Link
                                className="badge bg-primary bg-soft text-primary"
                                to={`/accounts/${invoice.customer}`}
                            >
                                <i className="fas fa-user me-1" />
                                {customerName}
                            </Link>
                            {invoice.supplier &&
                                <Link
                                    className="badge bg-warning bg-soft text-warning"
                                    to={`/accounts/${invoice.supplier}`}
                                >
                                    <i className="fas fa-file-export me-1" />
                                    {t("common.onBehalfOf")} {supplierName}
                                </Link>
                            }
                        </div>
                        <Link className="small text-muted" to={`/tenants/${invoice.tenant}`}>
                            <i className="dripicons-store me-1" />
                            <TenantName fetch={false} id={invoice.tenant} />
                        </Link>
                    </Col>
                    <Col md={2}>
                        <small className="d-block text-muted">{t("common.created")}</small>
                        {date(invoice.GetCreatedDate())}
                    </Col>
                    <Col md={2}>
                        {!invoice.quote && !invoice.draft && <small className="d-block text-muted">
                            {invoice.IsPayWhenPaid() ? t("invoice.invoiceRowIndex.paymentTerm") : t("invoice.invoiceRowIndex.deadline")}
                        </small>}
                        {status}
                    </Col>
                    <Col className="text-end" md={1}>
                        <strong>{currency(invoice.CalcTotalContents())}</strong>
                    </Col>
                    <Col className="text-end" md={1}>
                        <Link
                            aria-label={t("invoice.invoiceRowIndex.viewName", {name: invoice.GetDisplayName() || t("invoice.invoiceRowIndex.invoice")})}
                            className="btn btn-sm btn-light"
                            to={`/invoice/${invoice.id}`}
                        >
                            <i className="mdi mdi-chevron-right" />
                        </Link>
                    </Col>
                </Row>
            </div>

            <div className="d-md-none border-bottom py-3" style={style}>
                <div className="d-flex justify-content-between align-items-start gap-3">
                    <div className="min-w-0">
                        <Link className="text-body text-decoration-none d-block text-truncate" to={`/invoice/${invoice.id}`}>
                            <strong>{invoice.GetDisplayName() || <i>{t("common.noName")}</i>}</strong>
                        </Link>
                        <small className="text-muted">{date(invoice.GetCreatedDate())}</small>
                    </div>
                    <div className="text-end flex-shrink-0">
                        <strong className="d-block">{currency(invoice.CalcTotalContents())}</strong>
                        {status}
                    </div>
                </div>

                <div className="d-flex align-items-end justify-content-between gap-2 mt-2">
                    <div className="min-w-0">
                        <div className="d-flex flex-wrap gap-1">
                            <Link
                                className="badge bg-primary bg-soft text-primary text-truncate"
                                style={{ maxWidth: "100%" }}
                                to={`/accounts/${invoice.customer}`}
                            >
                                <i className="fas fa-user me-1" />
                                {customerName}
                            </Link>
                            {invoice.supplier && <Link
                                className="badge bg-warning bg-soft text-warning text-truncate"
                                style={{ maxWidth: "100%" }}
                                to={`/accounts/${invoice.supplier}`}
                            >
                                <i className="fas fa-file-export me-1" />
                                {t("common.onBehalfOf")} {supplierName}
                            </Link>}
                        </div>
                        <Link className="small text-muted d-block text-truncate mt-1" to={`/tenants/${invoice.tenant}`}>
                            <i className="dripicons-store me-1" />
                            <TenantName fetch={false} id={invoice.tenant} />
                        </Link>
                    </div>
                    <Link
                        aria-label={t("invoice.invoiceRowIndex.viewName", {name: invoice.GetDisplayName() || t("invoice.invoiceRowIndex.invoice")})}
                        className="btn btn-sm btn-light flex-shrink-0"
                        to={`/invoice/${invoice.id}`}
                    >
                        <i className="mdi mdi-chevron-right" />
                    </Link>
                </div>
            </div>
        </>
    );
};

export default InvoiceRowIndex;
